import { UserMapper } from "@/domain/mappers";
import {
  fixFrequency,
  getAppointmentStatusColor,
  getAppointmentStatusLabel,
  getFormattedAppointmentStatus,
  getFrequencyDateText,
} from "@/data/utils";
import {
  AppointmentRescheduleStatus,
  AppointmentStatuses,
  AppointmentTypes,
  DomainColors,
  FrequencyTypeLabels,
  FrequencyTypePluralLabels,
} from "@/domain/contracts";
import {
  Appointment,
  AppointmentChat,
  AppointmentPsychologist,
  AppointmentRecurrencyDetail,
  AppointmentUser,
  RecurrencyAppointmentItem,
  RescheduleRequestData,
} from "@/domain/entities";
import { FormatterService, StringFormatterService } from "@/domain/services";
import { payoutStatus } from "@/domain/utils/payoutUtils";
import moment from "moment";

import { AppointmentMapper } from "../../domain/mappers";
import { AppointmentInput } from "@/domain/dtos/inputs";
import { CreateUserRequest, UpdateAppointmentRequest } from "../dtos/requests";
import {
  AppointmentRecurrencyResponse,
  AppointmentResponse,
  PastAppointment,
  UserHomeInfoResponse,
  UserResponse,
} from "../dtos/responses";

export class AppointmentMapperImpl implements AppointmentMapper<
  AppointmentResponse,
  UpdateAppointmentRequest,
  AppointmentRecurrencyResponse
> {
  constructor(
    private readonly stringFormatter: StringFormatterService,
    private readonly cpfFormatter: FormatterService,
    private readonly userMapper: UserMapper<
      UserResponse,
      UserHomeInfoResponse,
      CreateUserRequest
    >,
    private readonly getSessionUserId?: () => number | undefined,
  ) {}

  toDomain(data: AppointmentResponse): Appointment {
    let rescheduleRequest: RescheduleRequestData | undefined;
    if (
      data.type === AppointmentTypes.scheduled &&
      data.reschedule_requests &&
      data.reschedule_requests.length &&
      data.reschedule_requests[0].status !==
        AppointmentRescheduleStatus.canceled
    ) {
      rescheduleRequest = this.prepareRescheduleRequest(
        data.reschedule_requests[0],
      );
    }

    let recurrency: any = null;

    if (data.recurrency) {
      const freq = fixFrequency(
        data.recurrency.frequency,
        data.recurrency.interval,
      );
      recurrency = {
        ...data.recurrency,
        frequencyLabel: FrequencyTypeLabels[freq],
        frequencyText: getFrequencyDateText(
          data.recurrency.frequency,
          data.recurrency.interval,
          data.recurrency.reference_date,
        ),
      };

      if (data?.recurrency.next_appointment_date) {
        recurrency.nextAppointmentDate = moment(
          data?.recurrency?.next_appointment_date?.date,
        ).format("DD/MM");
      }
    }

    // Mostra card de erro e esconde botões de ação
    const paymentError =
      (data.pg_status === "requires_payment_method" ||
        data.pg_status === "requires_action") &&
      data.payment_method === "card";

    const payment_requires_action = data.pg_status === "requires_action";

    const is_waiting_boleto_payment =
      data.payment_method === "boleto" &&
      (data.status == AppointmentStatuses.aguardando ||
        data.status == AppointmentStatuses.agendado);

    const user = this.userMapper.toPatientDomain(data.user);
    const psychologist = this.userMapper.toDomain(data.psychologist);

    let payout;
    if (data.payout) {
      const arrival_date = data.payout.main_payout_info.arrival_date;
      payout = {
        id: data.payout.id,
        status: data.payout.main_payout_info.status,
        date: data.payout.main_payout_info.date,
        value: data.payout.main_payout_info.value,
        arrival_date,
        net: data.payout.net,
        payout_id: data.payout.payout_id,
        fee: data.payout.fee,
        main_payout_info: {
          status: data.payout.main_payout_info.status,
          arrival_date,
        },
        status_config: payoutStatus(data.payout.main_payout_info.status, arrival_date),
        formatted_arrival_date: arrival_date
          ? moment(arrival_date).format("DD/MM/YYYY")
          : "",
      };
    }

    const status_label = getAppointmentStatusLabel(data);
    const status_color = getAppointmentStatusColor(data);
    const status_icon = getFormattedAppointmentStatus(data.status).icon || "";
    const price = `R$${data.value}`;
    const date = moment(data.date);
    const formatted_date = `${date.format("DD/MM/YY")}`;
    const formatted_time = `${date.format(`HH[h]mm`)} às ${date
      .add(50, "minutes")
      .format(`HH[h]mm`)}`;

    // Pacientes
    const has_chat =
      data.chat && data.chat?.status === "active" && !data.user?.deleted_at;

    let chat: AppointmentChat | undefined;
    if (has_chat) {
      chat = {
        ...data.chat,
        psychologist: {
          id: data.psychologist.id,
          name: data.psychologist.name,
        },
      };
    }

    const show_call_link =
      data.can_enter ||
      (data.can_enter &&
        (data.status === AppointmentStatuses["em andamento"] ||
          data.status !== AppointmentStatuses.aguardando));

    return {
      ...data,
      user,
      psychologist,
      payout,
      is_waiting_boleto_payment,
      recurrency,
      paymentError,
      payment_requires_action,
      rescheduleRequest,
      status_label,
      status_color,
      price,
      formatted_date,
      formatted_time,
      status_icon,
      chat,
      has_chat,
      show_call_link,
    };
  }

  toUpdateRequest(entity: AppointmentInput): UpdateAppointmentRequest {
    return {
      status: entity.status,
      reason_for_cancellation: entity.reason_for_cancellation,
      cancel_recurrency: entity.cancel_recurrency,
    };
  }

  private toAppointmentUser(data: UserResponse): AppointmentUser {
    return {
      id: data.id,
      name: data.deleted_at ? "Paciente removido" : data?.name,
      profile_image: data.profile_image,
      deleted_at: data.deleted_at || "",
      handicapped: data.handicapped,
      document_number: data.document_number
        ? this.cpfFormatter.format(data.document_number)
        : "",
    };
  }

  private toAppointmentPsychologist(
    data: UserResponse,
  ): AppointmentPsychologist {
    return {
      id: data.id,
      name: data.deleted_at ? "Paciente removido" : data?.name,
      profile_image: data.profile_image,
      deleted_at: data.deleted_at || "",
      crp: data.crp,
      rating: (data.avg_rating || 0).toFixed(1),
      appointment_price: data.appointment_price || 0,
    };
  }

  toRecurrencyDetail(
    data: AppointmentRecurrencyResponse,
  ): AppointmentRecurrencyDetail {
    const frequency = fixFrequency(data.frequency, data.interval);

    const title = `Sessões ${FrequencyTypePluralLabels[frequency]}`;

    const description =
      getFrequencyDateText(
        data.frequency,
        data.interval,
        data.reference_date,
        true,
      ) ?? "";

    const mapAppointmentItem = (
      item: PastAppointment,
    ): RecurrencyAppointmentItem => ({
      id: item.id,
      date: item.date,
      value: item.value,
      status: item.status,
      status_label: getAppointmentStatusLabel(item),
      status_color: getAppointmentStatusColor(item),
    });

    return {
      id: data.id,
      status: data.status,
      title,
      description,
      status_label: data.status === "active" ? "ativa" : "cancelada",
      status_color:
        data.status === "active"
          ? DomainColors.success
          : DomainColors.primary50,
      next_appointment: data.next_appointment
        ? mapAppointmentItem(data.next_appointment)
        : null,
      past_appointments: data.past_appointments.map(mapAppointmentItem),
    };
  }

  private prepareRescheduleRequest = (data: any): RescheduleRequestData => {
    const sessionUserId = this.getSessionUserId?.();
    const myRequest = sessionUserId === data.user_id;

    const formattedDate = this.stringFormatter.capitalizeFirst(
      `${moment(data?.new_date).format("DD/MM")} - ${moment(
        data?.new_date,
      ).format("dddd")}`,
    );

    const formattedTime = `das ${moment(data?.new_date).format(
      "HH[h]mm",
    )} às ${moment(data?.new_date).add(50, "minutes").format("HH[h]mm")}`;

    const descriptionDate = `dia ${moment(data?.new_date).format("DD/MM")}`;

    const descriptionTime = `${moment(data?.new_date).format(
      "HH[h]mm",
    )} às ${moment(data?.new_date).add(50, "minutes").format("HH[h]mm")}`;

    const userTypeLabel = myRequest ? "paciente" : "psi";

    const baseConfig = {
      formattedDate,
      formattedTime,
      myRequest,
      descriptionDate,
      descriptionTime,
      userTypeLabel,
    };

    const config: Record<string, Omit<RescheduleRequestData, "status">> = {
      pending: {
        ...baseConfig,
        id: data.id,
        pendingByMe: !myRequest,
        color: DomainColors.primary,
        bgColor: DomainColors.primary05,
        title: "Reagendamento pendente",
        icon: "clock",
        descriptionStatus: `encontra-se aguardando confirmação por ${!myRequest ? "parte do psi" : "você"}`,
      },
      rejected: {
        ...baseConfig,
        id: data.id,
        pendingByMe: false,
        color: DomainColors.warning,
        bgColor: DomainColors.warning05,
        title: "Reagendamento rejeitado",
        icon: "times-circle",
        descriptionStatus: `não foi confirmado por ${!myRequest ? "parte do psi" : "você"}`,
      },
      accepted: {
        ...baseConfig,
        id: data.id,
        pendingByMe: false,
        color: DomainColors.success,
        bgColor: DomainColors.success05,
        title: "Reagendamento confirmado",
        icon: "check-circle",
        descriptionStatus: `foi confirmado por ${!myRequest ? "parte do psi" : "você"}`,
      },
    };

    return {
      ...config[data?.status],
      status: data?.status,
    };
  };
}
